// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl';
import { useAppDispatch, useAppStore } from 'Hooks';
import { setHovered, clearHovered } from 'Features/fileSelection/fileSelectionSlice';
import { orderFootprintHits } from './footprintGeoJson';

const FILL_LAYER = 'file-footprints-fill';
const THROTTLE_MS = 50;

const BLOCKING_DRAW_MODES = ['draw_polygon', 'draw_line_string', 'draw_point', 'direct_select'];

interface DrawPresent {
  mode?: { name?: string };
  measurementMode?: boolean;
  createKml?: boolean;
  addToExistingKml?: boolean;
}

// Footprint gestures (hover, click, card) go fully dormant while a shape is
// actively being drawn or measured (D49/D51). The KML flow flags gate the mode
// check because the redux mode.name goes STALE: onDrawModeChange only mirrors
// mapbox-draw's return to simple_select inside an active create-KML flow, so a
// dismissed draw leaves mode.name at draw_polygon forever (AUT-6433-6436 area)
// and trusting it alone locked footprints out permanently.
export const footprintGesturesBlocked = (draw: DrawPresent): boolean => {
  if (draw.measurementMode) return true;
  if (!BLOCKING_DRAW_MODES.includes(draw.mode?.name || '')) return false;
  return !!(draw.createKml || draw.addToExistingKml);
};

const sameFiles = (a: IFileRef[], b: IFileRef[]): boolean =>
  a.length === b.length && a.every((f, i) => f.kind === b[i].kind && f.id === b[i].id);

export type FootprintClickHandler = (files: IFileRef[], lngLat: [number, number]) => void;

// Map -> state half of the bidirectional hover (modeled on MapKMLLayer's
// mousemove + queryRenderedFeatures). Only map-sourced hover is ever cleared
// here, so a sidebar-row hover is never fought by mouse movement on the map.
export const useFootprintInteractions = (onFootprintClick?: FootprintClickHandler) => {
  const { current: map } = useMap();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  // The click callback rides in a ref so the map listeners bind once per map.
  const clickHandlerRef = useRef<FootprintClickHandler | undefined>(onFootprintClick);
  clickHandlerRef.current = onFootprintClick;

  useEffect(() => {
    if (!map) return undefined;

    const clearMapHover = () => {
      const { hovered } = store.getState().fileSelection;
      if (hovered?.source === 'map') dispatch(clearHovered());
    };

    // A feature comes back once per vector tile it spans; dedupe by kind+id.
    const collectHits = (point: mapboxgl.Point): IFileRef[] => {
      const hits = map.queryRenderedFeatures(point, { layers: [FILL_LAYER] });
      // A kind unchecked in the switch card's list takes no hits (D78). Its
      // features are already gone from the source; this guard covers the
      // frames where the map still renders the pre-filter tiles.
      const hiddenKinds = store.getState().map.common.hiddenFootprintKinds;
      const seen = new Set<string>();
      const files: Array<IFileRef & { name: string }> = [];
      hits.forEach((hit) => {
        if (!hit.properties) return;
        const kind = hit.properties.kind as FileSelectionKind;
        const id = hit.properties.id as string;
        const name = hit.properties.name as string;
        if (hiddenKinds.includes(kind)) return;
        if (seen.has(`${kind}-${id}`)) return;
        seen.add(`${kind}-${id}`);
        files.push({ kind, id, name });
      });
      return orderFootprintHits(files).map(({ kind, id }) => ({ kind, id }));
    };

    let lastMove = 0;
    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const now = Date.now();
      if (now - lastMove < THROTTLE_MS) return;
      lastMove = now;
      try {
        if (!map.getLayer(FILL_LAYER)) return;
        // Dormant while drawing/measuring: no pointer cursor, no highlight,
        // no fade churn under the tool (D51).
        if (footprintGesturesBlocked(store.getState().map.draw.present)) {
          map.getCanvas().style.cursor = '';
          clearMapHover();
          return;
        }
        const ordered = collectHits(e.point);
        map.getCanvas().style.cursor = ordered.length ? 'pointer' : '';
        // Click-only level: hover is muted - no highlight, captions or row
        // sync - while the pointer cursor and clicks keep working.
        if (!ordered.length || store.getState().map.common.extentsHoverMuted) {
          clearMapHover();
          return;
        }
        const { hovered } = store.getState().fileSelection;
        if (hovered?.source !== 'map' || !sameFiles(hovered.files, ordered)) {
          dispatch(setHovered({ source: 'map', files: ordered }));
        }
      } catch {
        // Map may be in a transitional state (style swap, teardown)
      }
    };

    const onMouseOut = () => {
      try {
        map.getCanvas().style.cursor = '';
      } catch {
        // map torn down
      }
      clearMapHover();
    };

    const onClick = (e: mapboxgl.MapMouseEvent) => {
      const handler = clickHandlerRef.current;
      if (!handler) return;
      try {
        if (footprintGesturesBlocked(store.getState().map.draw.present)) return;
        if (!map.getLayer(FILL_LAYER)) return;
        // The hook only reports; the single-click-toggle vs stack-card
        // decision belongs to the component (D55).
        handler(collectHits(e.point), [e.lngLat.lng, e.lngLat.lat]);
      } catch {
        // Map may be in a transitional state (style swap, teardown)
      }
    };

    map.on('mousemove', onMouseMove);
    map.on('mouseout', onMouseOut);
    map.on('click', onClick);
    return () => {
      try {
        map.off('mousemove', onMouseMove);
        map.off('mouseout', onMouseOut);
        map.off('click', onClick);
      } catch {
        // map destroyed
      }
    };
  }, [map]);
};

export default useFootprintInteractions;
